import type { ReactNode } from 'react';
import { cn } from '@clarix/ui';
import { ClaraIcon } from '../ClaraIcon';
import type { HomeStarter } from './types';

export interface HomeHeroProps {
  /** Greeting block, centred above the mark — the first thing read. */
  greeting?: ReactNode;
  /** The assistant's name, set in caps as the hero title. */
  title: string;
  /** One line under the title — the question the person is being asked. */
  subtitle?: string;
  /** Suggestion pills, so nobody faces a blank box. */
  starters?: readonly HomeStarter[];
  /** Fallback for a starter without its own `onSelect`. */
  onStarterSelect?: (starter: HomeStarter) => void;
  className?: string;
}

/**
 * The empty state of the common home — assistant mark, name, one question,
 * and a handful of starters. Shown until the conversation begins.
 */
export function HomeHero({
  greeting,
  title,
  subtitle,
  starters = [],
  onStarterSelect,
  className,
}: HomeHeroProps) {
  return (
    <div
      className={cn(
        'flex h-full flex-col items-center justify-center px-6 py-8 text-center',
        className,
      )}
    >
      {greeting ? <div className="mb-7">{greeting}</div> : null}

      {/* Mark + wordmark, with a slow sheen passing over the pair. */}
      <div className="relative isolate mb-4 flex items-center gap-1 overflow-hidden px-1.5 py-0.5">
        {/* ClaraIcon is the one shared assistant mark — scaled up here rather
            than re-drawn (UI rule 3: add the delta at the call site). */}
        <ClaraIcon className="h-14 w-14 [&_svg]:h-9 [&_svg]:w-9" />
        <h1 className="text-primary text-[2.4rem] font-extrabold leading-none tracking-[0.08em]">
          {title}
          <span className="animate-pulse" aria-hidden>
            .
          </span>
        </h1>
        <span
          aria-hidden
          // `animate-shimmer` resolves to a translateX sweep (the keyframe is
          // redefined in packages/ui/src/styles.css), so this overlay slides
          // across the wordmark. No bg-position sizing — it would do nothing.
          className="animate-shimmer via-surface/70 pointer-events-none absolute inset-0 z-10 bg-gradient-to-r from-transparent to-transparent mix-blend-overlay"
        />
      </div>

      {subtitle ? (
        <p className="text-muted-foreground max-w-[400px] text-base leading-relaxed">{subtitle}</p>
      ) : null}

      {starters.length > 0 ? (
        <div className="mt-6 flex max-w-[560px] flex-wrap justify-center gap-2.5">
          {starters.map((starter) => (
            <button
              key={starter.id}
              type="button"
              onClick={() => (starter.onSelect ? starter.onSelect() : onStarterSelect?.(starter))}
              className="border-border bg-surface/85 hover:border-primary/25 hover:text-primary rounded-full border px-4 py-2.5 text-[13px] font-medium text-[rgb(var(--ink-2))] shadow-sm backdrop-blur-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--primary))]/30"
            >
              {starter.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
